import React, { useState, useEffect } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { User } from '../../types';
import { apiService } from '../../services/api';
import { formatPhoneNumber, US_DISPLAY_PHONE_PATTERN } from '../../utils/phoneFormatUtils';
import toast from 'react-hot-toast';

export interface BookingPhoneRequiredModalProps {
  isOpen: boolean;
  brandColor: string;
  businessName: string;
  onSaved: (user: User) => void;
}

const BookingPhoneRequiredModal: React.FC<BookingPhoneRequiredModalProps> = ({
  isOpen,
  brandColor,
  businessName,
  onSaved,
}) => {
  const [phone, setPhone] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPhone('');
    setFieldError(null);
    setIsSaving(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const displayName = (businessName || 'this business').trim() || 'this business';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = phone.trim();
    if (!v) {
      setFieldError('Please enter your phone number.');
      return;
    }
    if (!US_DISPLAY_PHONE_PATTERN.test(v)) {
      setFieldError('Enter a valid number in the format (555) 123-4567.');
      return;
    }
    setFieldError(null);
    setIsSaving(true);
    try {
      const updated = await apiService.updateCurrentUser({ phone: v });
      toast.success('Phone number saved.');
      onSaved(updated);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        'Could not save your phone number. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not save your phone number.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-phone-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${brandColor}1A` }}
              >
                <Info className="w-6 h-6" style={{ color: brandColor }} aria-hidden />
              </div>
            </div>
            <div>
              <h3 id="booking-phone-modal-title" className="text-lg font-medium text-gray-900">
                Phone number required
              </h3>
            </div>
          </div>

          <div className="mb-5 space-y-3">
            <p className="text-sm text-gray-700 leading-relaxed">
              <span className="font-medium text-gray-900">{displayName}</span> uses your phone number to confirm
              appointments, share updates, and reach you quickly if plans change. Please add a mobile or primary
              contact number to continue. Your information is handled securely and is used only for communication
              related to your visits.
            </p>
            <div>
              <label htmlFor="booking-phone-input" className="block text-sm font-medium text-gray-700 mb-1.5">
                Phone number <span className="text-red-500">*</span>
              </label>
              <input
                id="booking-phone-input"
                type="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                className={`w-full px-3 py-2.5 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-shadow ${
                  fieldError ? 'border-red-500 focus:ring-red-500/25' : 'focus:ring-gray-200'
                }`}
                style={!fieldError ? { borderColor: `${brandColor}73` } : undefined}
                value={phone}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setPhone(formatted);
                  if (fieldError) setFieldError(null);
                }}
                disabled={isSaving}
              />
              {fieldError && <p className="mt-1.5 text-sm text-red-600">{fieldError}</p>}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="min-w-[160px] px-4 py-2.5 text-white text-sm font-medium rounded-lg transition-opacity inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: brandColor, boxShadow: `0 8px 18px ${brandColor}4d` }}
            >
              {isSaving ? (
                'Saving…'
              ) : (
                <>
                  Save and continue
                  <ChevronRight className="w-4 h-4 flex-shrink-0" aria-hidden />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingPhoneRequiredModal;
